import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, PhoneCall, Bot, Database, MessageSquare, Radio, RotateCcw, Play, Pause, Sparkles } from 'lucide-react';
// =================== Stage coordinates (matches Slide 1) ===================
const STAGE_W = 1280;
const STAGE_H = 720;
const CENTER = {
  x: 640,
  y: 320
};
const CARD_W = 210;
const CARD_H = 72;
const CENTER_W = 230;
const CENTER_H = 86;
// VEXU large block (used after the collapse)
const VEXU_W = 290;
const VEXU_H = 110;
const SYSTEMS = [{
  id: 'pbx',
  label: 'PBX / Call Center',
  sub: 'Telephony backbone',
  icon: PhoneCall,
  x: 640,
  y: 130
}, {
  id: 'ai',
  label: 'AI Agent Provider',
  sub: 'LLM + Voice',
  icon: Bot,
  x: 980,
  y: 240
}, {
  id: 'crm',
  label: 'CRM System',
  sub: 'Customer data',
  icon: Database,
  x: 980,
  y: 410
}, {
  id: 'whatsapp',
  label: 'WhatsApp API',
  sub: 'Messaging channel',
  icon: MessageSquare,
  x: 300,
  y: 410
}, {
  id: 'telecom',
  label: 'Telecom Provider',
  sub: 'Carrier & numbers',
  icon: Radio,
  x: 300,
  y: 240
}] as const;
// Edge-aware endpoint computation
function edgePoint(cx: number, cy: number, tx: number, ty: number, halfW: number, halfH: number) {
  const dx = tx - cx;
  const dy = ty - cy;
  if (dx === 0 && dy === 0) return {
    x: tx,
    y: ty
  };
  const sx = halfW / Math.abs(dx || 1e-9);
  const sy = halfH / Math.abs(dy || 1e-9);
  const s = Math.min(sx, sy);
  return {
    x: tx - dx * s,
    y: ty - dy * s
  };
}
const LINES = SYSTEMS.map(s => {
  const start = edgePoint(s.x, s.y, CENTER.x, CENTER.y, CENTER_W / 2, CENTER_H / 2);
  const end = edgePoint(CENTER.x, CENTER.y, s.x, s.y, CARD_W / 2, CARD_H / 2);
  const length = Math.hypot(end.x - start.x, end.y - start.y);
  return {
    id: s.id,
    start,
    end,
    length
  };
});
// Clean two-node layout (after collapse)
const CLEAN_BUSINESS = {
  x: 360,
  y: 380
};
const CLEAN_VEXU = {
  x: 900,
  y: 380
};
// Single line between Business and VEXU (edge to edge)
const CLEAN_LINE = (() => {
  const start = edgePoint(CLEAN_VEXU.x, CLEAN_VEXU.y, CLEAN_BUSINESS.x, CLEAN_BUSINESS.y, CENTER_W / 2, CENTER_H / 2);
  const end = edgePoint(CLEAN_BUSINESS.x, CLEAN_BUSINESS.y, CLEAN_VEXU.x, CLEAN_VEXU.y, VEXU_W / 2, VEXU_H / 2);
  const length = Math.hypot(end.x - start.x, end.y - start.y);
  return {
    start,
    end,
    length
  };
})();
// Micro-tags around VEXU
const VEXU_TAGS: Array<{
  label: string;
  dx: number;
  dy: number;
}> = [{
  label: 'Unified AI',
  dx: 0,
  dy: -90
}, {
  label: 'Plug & Play',
  dx: 170,
  dy: -45
}, {
  label: 'No PBX Required',
  dx: 170,
  dy: 45
}, {
  label: 'No Multi-Vendor Setup',
  dx: 0,
  dy: 95
}, {
  label: 'No-Code · Low-Code',
  dx: -170,
  dy: 45
}];
// =================== Scenes ===================
interface Scene {
  id: 'inherit' | 'focus' | 'vexu' | 'collapse' | 'clean' | 'calm' | 'impact';
  chapter: string;
  title: string;
  caption: string;
  duration: number;
}
const SCENES: Scene[] = [{
  id: 'inherit',
  chapter: 'Chapter 01',
  title: 'The Reality',
  caption: "You've seen the chaos.",
  duration: 3
}, {
  id: 'focus',
  chapter: 'Chapter 02',
  title: 'Pause',
  caption: "There's a better way.",
  duration: 3
}, {
  id: 'vexu',
  chapter: 'Chapter 03',
  title: 'VEXU Arrives',
  caption: 'One platform replaces them all.',
  duration: 4
}, {
  id: 'collapse',
  chapter: 'Chapter 04',
  title: 'The Merge',
  caption: 'Five vendors. One platform.',
  duration: 7
}, {
  id: 'clean',
  chapter: 'Chapter 05',
  title: 'Clean Architecture',
  caption: 'One integration. One owner.',
  duration: 6
}, {
  id: 'calm',
  chapter: 'Chapter 06',
  title: 'What You Get',
  caption: 'Unified, simple, in your control.',
  duration: 4
}, {
  id: 'impact',
  chapter: 'Chapter 07',
  title: 'Complete Control',
  caption: 'From complexity to clarity.',
  duration: 5
}];
const TOTAL_DURATION = SCENES.reduce((a, s) => a + s.duration, 0);
// Absolute timing
const T = {
  STAGE_IN: 0.2,
  DIM: 3.0,
  BETTER: 3.6,
  GLOW_IN: 6.2,
  VEXU_LOGO: 7.0,
  COLLAPSE_START: 10.0,
  COLLAPSE_STEP: 0.8,
  PLATFORM_LABEL: 14.5,
  CLEAN_REVEAL: 17.2,
  CLEAN_LINES: 19.0,
  TAGS: 23.5,
  IMPACT_1: 27.0,
  IMPACT_2: 28.4
};
// =================== Main ===================
export const SLIDE_2_DURATION = TOTAL_DURATION;
export function PresentationSlide2({
  externalElapsed,
  hideChrome
}: {
  externalElapsed?: number;
  hideChrome?: boolean;
} = {}) {
  const isControlled = externalElapsed !== undefined;
  const [runKey, setRunKey] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [internalElapsed, setElapsed] = useState(0);
  const startedAtRef = useRef<number>(Date.now());
  const pausedAtRef = useRef<number>(0);
  useEffect(() => {
    if (isControlled) return;
    startedAtRef.current = Date.now();
    pausedAtRef.current = 0;
    setElapsed(0);
  }, [runKey, isControlled]);
  useEffect(() => {
    if (isControlled) return;
    if (!playing) {
      pausedAtRef.current = internalElapsed;
      return;
    }
    startedAtRef.current = Date.now() - pausedAtRef.current * 1000;
    let raf: number;
    const tick = () => {
      const t = (Date.now() - startedAtRef.current) / 1000;
      setElapsed(Math.min(t, TOTAL_DURATION));
      if (t < TOTAL_DURATION) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, runKey, isControlled]);
  const elapsed = isControlled ? Math.max(0, Math.min(externalElapsed!, TOTAL_DURATION)) : internalElapsed;
  const currentScene = (() => {
    let acc = 0;
    for (const s of SCENES) {
      if (elapsed < acc + s.duration) return {
        scene: s,
        sceneStart: acc
      };
      acc += s.duration;
    }
    return {
      scene: SCENES[SCENES.length - 1],
      sceneStart: TOTAL_DURATION - SCENES[SCENES.length - 1].duration
    };
  })();
  return <div className="relative w-screen h-screen overflow-hidden bg-[#06080F] text-white font-sans flex items-center justify-center" data-id="element-1452">
      {/* Backgrounds */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#06080F] via-[#0A0F1F] to-[#06080F]" data-id="element-1453" />
      <motion.div className="absolute inset-0" animate={{
      background: ['radial-gradient(circle at 30% 40%, rgba(16,217,106,0.12) 0%, transparent 55%), radial-gradient(circle at 70% 70%, rgba(34,211,238,0.07) 0%, transparent 50%)', 'radial-gradient(circle at 70% 30%, rgba(16,217,106,0.12) 0%, transparent 55%), radial-gradient(circle at 30% 70%, rgba(34,211,238,0.07) 0%, transparent 50%)', 'radial-gradient(circle at 30% 40%, rgba(16,217,106,0.12) 0%, transparent 55%), radial-gradient(circle at 70% 70%, rgba(34,211,238,0.07) 0%, transparent 50%)']
    }} transition={{
      duration: 14,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-1454" />
      <div className="absolute inset-0 opacity-[0.10]" style={{
      backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.18) 1px, transparent 1px)',
      backgroundSize: '36px 36px'
    }} data-id="element-1455" />
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,0.7) 100%)'
    }} data-id="element-1456" />

      {/* Top controls */}
      {!hideChrome && <div className="absolute top-6 right-6 z-50 flex items-center gap-2" data-id="element-1457">
          <button onClick={() => setPlaying(p => !p)} className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.05] hover:bg-white/[0.1] backdrop-blur-xl border border-white/10 rounded-full text-xs font-semibold text-white/80 hover:text-white transition-all" data-id="element-1458">
            {playing ? <>
                <Pause className="w-3.5 h-3.5" data-id="element-1459" /> Pause
              </> : <>
                <Play className="w-3.5 h-3.5" data-id="element-1460" /> Play
              </>}
          </button>
          <button onClick={() => {
        setRunKey(k => k + 1);
        setPlaying(true);
      }} className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.05] hover:bg-white/[0.1] backdrop-blur-xl border border-white/10 rounded-full text-xs font-semibold text-white/80 hover:text-white transition-all" data-id="element-1461">
            <RotateCcw className="w-3.5 h-3.5" data-id="element-1462" /> Replay
          </button>
        </div>}

      {/* Chapter indicator (hidden — replaced by deck-level Vexu.ai brand) */}
      <AnimatePresence mode="wait" data-id="element-1463">
        <motion.div key={currentScene.scene.id} initial={{
        opacity: 0,
        x: -12
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -12
      }} transition={{
        duration: 0.6
      }} className="absolute top-6 left-6 z-50 hidden" data-id="element-1464">
          <div className="flex items-center gap-3" data-id="element-1465">
            <div className="w-8 h-px bg-emerald-400/70" data-id="element-1466" />
            <div data-id="element-1467">
              <div className="text-[10px] font-bold tracking-[0.3em] text-emerald-400 uppercase" data-id="element-1468">
                Slide 02 · {currentScene.scene.chapter}
              </div>
              <div className="text-sm font-display text-white/90 tracking-tight mt-0.5" data-id="element-1469">
                {currentScene.scene.title}
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      <Stage elapsed={elapsed} caption={currentScene.scene.caption} captionKey={currentScene.scene.id} data-id="element-1470" />

      {/* Bottom progress bar */}
      {!hideChrome && <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-6" data-id="element-1471">
          <div className="flex items-center gap-2 mb-2.5" data-id="element-1472">
            {SCENES.map((s, i) => {
          const sceneStart = SCENES.slice(0, i).reduce((a, b) => a + b.duration, 0);
          const sceneEnd = sceneStart + s.duration;
          const isActive = elapsed >= sceneStart && elapsed < sceneEnd;
          const isDone = elapsed >= sceneEnd;
          return <div key={s.id} className="flex-1 h-1 rounded-full bg-white/[0.08] overflow-hidden" data-id="element-1473">
                  <motion.div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-300" animate={{
              width: isDone ? '100%' : isActive ? `${(elapsed - sceneStart) / s.duration * 100}%` : '0%'
            }} transition={{
              duration: 0.1,
              ease: 'linear'
            }} data-id="element-1474" />
                </div>;
        })}
          </div>
          <div className="flex items-center justify-between text-[9px] font-bold tracking-[0.25em] text-white/45 uppercase" data-id="element-1475">
            <span data-id="element-1476">Slide 02 · The Resolution</span>
            <span className="font-mono" data-id="element-1477">
              {fmt(elapsed)} / {fmt(TOTAL_DURATION)}
            </span>
          </div>
        </div>}
    </div>;
}
function fmt(s: number) {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}
// =================== STAGE ===================
function Stage({
  elapsed,
  caption,
  captionKey
}: {
  elapsed: number;
  caption: string;
  captionKey: string;
}) {
  const stageVisible = elapsed >= T.STAGE_IN;
  const dimming = elapsed >= T.DIM;
  const betterText = elapsed >= T.BETTER;
  const glowIn = elapsed >= T.GLOW_IN;
  const vexuLogo = elapsed >= T.VEXU_LOGO;
  const cleanReveal = elapsed >= T.CLEAN_REVEAL;
  const cleanLines = elapsed >= T.CLEAN_LINES;
  const tagsVisible = elapsed >= T.TAGS;
  // Per-system absorption progress (0 = at original position, 1 = fully absorbed into center)
  const sysAbsorb = SYSTEMS.map((_, i) => {
    const t0 = T.COLLAPSE_START + i * T.COLLAPSE_STEP;
    const t = (elapsed - t0) / 0.9;
    return Math.max(0, Math.min(1, t));
  });
  // Connection-line dissolve progress (0 = visible, 1 = gone) — slightly ahead of system absorb
  const lineDissolve = SYSTEMS.map((_, i) => {
    const t0 = T.COLLAPSE_START + i * T.COLLAPSE_STEP - 0.15;
    const t = (elapsed - t0) / 0.7;
    return Math.max(0, Math.min(1, t));
  });
  // Has the entire collapse finished?
  const collapseDone = elapsed >= T.COLLAPSE_START + SYSTEMS.length * T.COLLAPSE_STEP + 0.5;
  // Center transitions FROM "Your Business" TO "VEXU AI"
  const showVexuAtCenter = vexuLogo && !cleanReveal;
  const showLargePlatform = elapsed >= T.PLATFORM_LABEL && !cleanReveal;
  // After clean reveal, the 5-system layout disappears entirely
  const hideOriginalLayout = cleanReveal;
  return <div className="relative" style={{
    width: 'min(100vw, calc(100vh * 16 / 9))',
    height: 'min(100vh, calc(100vw * 9 / 16))',
    maxWidth: '100vw',
    maxHeight: '100vh'
  }} data-id="element-1478">
      <div className="absolute inset-0" data-id="element-1479">
        <div className="absolute" style={{
        width: STAGE_W,
        height: STAGE_H,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%) scale(var(--stage-scale))',
        transformOrigin: 'center center'
      }} data-id="element-1480">
          <ScaleVar data-id="element-1481" />

          {/* Subtle global dim during the focus phase (before VEXU logo lands) */}
          <motion.div className="absolute inset-0 pointer-events-none" initial={{
          opacity: 0
        }} animate={{
          opacity: dimming && !vexuLogo ? 0.55 : 0
        }} transition={{
          duration: 1.0,
          ease: [0.16, 1, 0.3, 1]
        }} style={{
          background: 'rgba(6,8,15,0.85)',
          zIndex: 35
        }} data-id="element-1482" />

          {/* "There's a better way." centered text during focus phase */}
          <AnimatePresence data-id="element-1483">
            {betterText && !vexuLogo && <motion.div key="better" initial={{
            opacity: 0,
            scale: 0.95,
            filter: 'blur(8px)'
          }} animate={{
            opacity: 1,
            scale: 1,
            filter: 'blur(0px)'
          }} exit={{
            opacity: 0,
            scale: 1.04,
            filter: 'blur(6px)'
          }} transition={{
            duration: 0.9,
            ease: [0.16, 1, 0.3, 1]
          }} className="absolute inset-0 flex items-center justify-center" style={{
            zIndex: 40
          }} data-id="element-1484">
                <div className="text-center" data-id="element-1485">
                  <div className="text-[11px] font-bold tracking-[0.35em] text-emerald-400 uppercase mb-5" data-id="element-1486">
                    A Pause
                  </div>
                  <h2 className="font-display text-5xl md:text-6xl lg:text-7xl text-white tracking-tight font-medium" data-id="element-1487">
                    There's a better way.
                  </h2>
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* TOP CAPTION — appears after VEXU is introduced */}
          {vexuLogo && <div className="absolute" style={{
          top: 24,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          zIndex: 25
        }} data-id="element-1488">
              <AnimatePresence mode="wait" data-id="element-1489">
                <motion.div key={captionKey} initial={{
              opacity: 0,
              y: 12,
              filter: 'blur(8px)'
            }} animate={{
              opacity: 1,
              y: 0,
              filter: 'blur(0px)'
            }} exit={{
              opacity: 0,
              y: -12,
              filter: 'blur(8px)'
            }} transition={{
              duration: 0.7,
              ease: [0.16, 1, 0.3, 1]
            }} className="px-7 py-2.5 rounded-full bg-white/[0.06] backdrop-blur-2xl border border-white/15 shadow-[0_8px_40px_rgba(0,0,0,0.5)]" data-id="element-1490">
                  <p className="font-display text-lg md:text-xl text-white tracking-tight whitespace-nowrap" data-id="element-1491">
                    {caption}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>}

          {/* SVG layer for connection lines */}
          <svg className="absolute pointer-events-none" width={STAGE_W} height={STAGE_H} viewBox={`0 0 ${STAGE_W} ${STAGE_H}`} style={{
          top: 0,
          left: 0,
          overflow: 'visible',
          zIndex: 30
        }} data-id="element-1492">
            {/* Original 5 connection lines — present from start, dissolve during collapse */}
            {!hideOriginalLayout && LINES.map((ln, i) => {
            const dissolve = lineDissolve[i];
            const baseOpacity = stageVisible ? 0.55 : 0;
            return <g key={`orig-${ln.id}`} data-id="element-1493">
                    <motion.line x1={ln.start.x} y1={ln.start.y} x2={ln.end.x} y2={ln.end.y} stroke="#10D96A" strokeWidth={6} strokeOpacity={0.14} fill="none" strokeLinecap="round" animate={{
                opacity: baseOpacity * (1 - dissolve)
              }} transition={{
                duration: 0.4,
                ease: 'easeOut'
              }} data-id="element-1494" />
                    <motion.line x1={ln.start.x} y1={ln.start.y} x2={ln.end.x} y2={ln.end.y} stroke="#34D399" strokeWidth={2} fill="none" strokeLinecap="round" animate={{
                opacity: baseOpacity * (1 - dissolve) * 0.9
              }} transition={{
                duration: 0.4,
                ease: 'easeOut'
              }} data-id="element-1495" />
                  </g>;
          })}

            {/* Clean single line: Business <-> VEXU */}
            {cleanReveal && (() => {
            const ln = CLEAN_LINE;
            return <g data-id="element-1496">
                    <motion.line x1={ln.start.x} y1={ln.start.y} x2={ln.end.x} y2={ln.end.y} stroke="#10D96A" strokeWidth={9} strokeOpacity={0.2} fill="none" strokeLinecap="round" strokeDasharray={ln.length} initial={{
                strokeDashoffset: ln.length,
                opacity: 0
              }} animate={{
                strokeDashoffset: 0,
                opacity: 1
              }} transition={{
                duration: 1.0,
                ease: [0.16, 1, 0.3, 1]
              }} data-id="element-1497" />
                    <motion.line x1={ln.start.x} y1={ln.start.y} x2={ln.end.x} y2={ln.end.y} stroke="#6EE7B7" strokeWidth={2.5} fill="none" strokeLinecap="round" strokeOpacity={0.95} strokeDasharray={ln.length} initial={{
                strokeDashoffset: ln.length,
                opacity: 0
              }} animate={{
                strokeDashoffset: 0,
                opacity: 1
              }} transition={{
                duration: 1.0,
                ease: [0.16, 1, 0.3, 1]
              }} data-id="element-1498" />
                    {/* Anchor dots */}
                    <motion.circle cx={ln.start.x} cy={ln.start.y} r={4} fill="#10D96A" initial={{
                opacity: 0,
                scale: 0
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                delay: 0.6,
                duration: 0.4
              }} data-id="element-1499" />
                    <motion.circle cx={ln.end.x} cy={ln.end.y} r={4} fill="#10D96A" initial={{
                opacity: 0,
                scale: 0
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                delay: 0.6,
                duration: 0.4
              }} data-id="element-1500" />
                  </g>;
          })()}
          </svg>

          {/* Center node — "Your Business" → "VEXU AI" → big "VEXU Platform" → fades when clean reveal */}
          {!hideOriginalLayout && <PositionedNode x={CENTER.x} y={CENTER.y} data-id="element-1501">
              <motion.div initial={{
            scale: 0,
            opacity: 0
          }} animate={{
            scale: stageVisible ? 1 : 0,
            opacity: stageVisible ? 1 : 0
          }} transition={{
            type: 'spring',
            stiffness: 220,
            damping: 22
          }} className="relative" data-id="element-1502">
                {/* Soft VEXU glow that grows in during the introduction */}
                <motion.div className="absolute -inset-12 rounded-full" initial={{
              opacity: 0,
              scale: 0.6
            }} animate={{
              opacity: glowIn ? 0.55 : 0,
              scale: glowIn ? showLargePlatform ? 1.6 : 1 : 0.6
            }} transition={{
              duration: 1.4,
              ease: [0.16, 1, 0.3, 1]
            }} style={{
              background: 'radial-gradient(circle, rgba(16,217,106,0.55) 0%, rgba(16,217,106,0) 70%)',
              filter: 'blur(8px)'
            }} data-id="element-1503" />

                {/* The card itself — morphs from Your Business → VEXU AI → larger VEXU Platform block */}
                <motion.div animate={{
              width: showLargePlatform ? VEXU_W : CENTER_W,
              height: showLargePlatform ? VEXU_H : CENTER_H
            }} transition={{
              duration: 1.0,
              ease: [0.16, 1, 0.3, 1]
            }} className="relative rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-[0_0_70px_rgba(16,217,106,0.55)] border border-emerald-300/50 flex items-center gap-3 px-7 py-4" data-id="element-1504">
                  {/* Subtle pulse ring */}
                  <motion.div animate={{
                scale: [1, 1.18, 1],
                opacity: [0.45, 0, 0.45]
              }} transition={{
                duration: 2.6,
                repeat: Infinity,
                ease: 'easeOut'
              }} className="absolute inset-0 rounded-2xl border border-emerald-300/50" data-id="element-1505" />

                  <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center shrink-0" data-id="element-1506">
                    {showVexuAtCenter || showLargePlatform ? <Sparkles className="w-6 h-6 text-white" strokeWidth={2.2} data-id="element-1507" /> : <Building2 className="w-6 h-6 text-white" strokeWidth={2.2} data-id="element-1508" />}
                  </div>

                  <div className="min-w-0 relative" data-id="element-1509">
                    <AnimatePresence mode="wait" data-id="element-1510">
                      {!showVexuAtCenter && !showLargePlatform && <motion.div key="biz" initial={{
                    opacity: 0,
                    y: 6
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -6
                  }} transition={{
                    duration: 0.5
                  }} data-id="element-1511">
                          <div className="text-[10px] font-bold tracking-[0.25em] text-white/80 uppercase" data-id="element-1512">
                            Core
                          </div>
                          <div className="text-lg font-semibold text-white tracking-tight leading-tight whitespace-nowrap" data-id="element-1513">
                            Your Business
                          </div>
                        </motion.div>}
                      {showVexuAtCenter && !showLargePlatform && <motion.div key="vexu" initial={{
                    opacity: 0,
                    y: 6,
                    filter: 'blur(6px)'
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    filter: 'blur(0px)'
                  }} exit={{
                    opacity: 0,
                    y: -6,
                    filter: 'blur(6px)'
                  }} transition={{
                    duration: 0.6,
                    ease: [0.16, 1, 0.3, 1]
                  }} data-id="element-1514">
                          <div className="text-[10px] font-bold tracking-[0.28em] text-white/85 uppercase" data-id="element-1515">
                            Unified Platform
                          </div>
                          <div className="text-xl font-bold text-white tracking-tight leading-tight whitespace-nowrap" data-id="element-1516">
                            VEXU AI
                          </div>
                        </motion.div>}
                      {showLargePlatform && <motion.div key="platform" initial={{
                    opacity: 0,
                    y: 6,
                    filter: 'blur(6px)'
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    filter: 'blur(0px)'
                  }} exit={{
                    opacity: 0,
                    y: -6,
                    filter: 'blur(6px)'
                  }} transition={{
                    duration: 0.7,
                    ease: [0.16, 1, 0.3, 1]
                  }} data-id="element-1517">
                          <div className="text-[10px] font-bold tracking-[0.32em] text-white/85 uppercase mb-0.5" data-id="element-1518">
                            One Platform
                          </div>
                          <div className="text-2xl font-bold text-white tracking-tight leading-tight whitespace-nowrap" data-id="element-1519">
                            VEXU Platform
                          </div>
                          <div className="text-[10px] font-medium tracking-[0.2em] text-emerald-100/80 mt-1 uppercase" data-id="element-1520">
                            All systems · One place
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </motion.div>
              </motion.div>
            </PositionedNode>}

          {/* System cards — animate from their pentagon position INTO the center, then unmount */}
          {!hideOriginalLayout && SYSTEMS.map((s, i) => {
          const Icon = s.icon;
          const absorb = sysAbsorb[i];
          // Interpolate position from (s.x, s.y) toward CENTER
          const lerpX = s.x + (CENTER.x - s.x) * absorb;
          const lerpY = s.y + (CENTER.y - s.y) * absorb;
          const fullyAbsorbed = absorb >= 1;
          if (fullyAbsorbed) return null;
          return <PositionedNode key={s.id} x={lerpX} y={lerpY} data-id="element-1521">
                  <motion.div initial={{
              opacity: 0,
              scale: 0.85,
              y: 10
            }} animate={{
              opacity: stageVisible ? 1 - absorb * 0.95 : 0,
              scale: stageVisible ? 1 - absorb * 0.4 : 0.85,
              y: stageVisible ? 0 : 10
            }} transition={{
              duration: 0.4,
              ease: [0.16, 1, 0.3, 1]
            }} className="relative" style={{
              width: CARD_W,
              height: CARD_H
            }} data-id="element-1522">
                    <div className="absolute -inset-3 bg-emerald-500/[0.10] rounded-2xl blur-xl" data-id="element-1523" />
                    <div className="relative rounded-xl bg-[#0F172A]/95 backdrop-blur-xl border border-white/15 shadow-[0_8px_32px_rgba(0,0,0,0.6)] flex items-center gap-2.5 px-4" style={{
                width: CARD_W,
                height: CARD_H
              }} data-id="element-1524">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-white/15 to-white/[0.03] border border-white/10 flex items-center justify-center shrink-0" data-id="element-1525">
                        <Icon className="w-4 h-4 text-emerald-300" strokeWidth={2} data-id="element-1526" />
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-1527">
                        <div className="text-[13px] font-semibold text-white tracking-tight truncate" data-id="element-1528">
                          {s.label}
                        </div>
                        <div className="text-[10px] text-white/50 font-medium tracking-wide truncate" data-id="element-1529">
                          {s.sub}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </PositionedNode>;
        })}

          {/* CLEAN LAYOUT — Business + VEXU side by side */}
          {cleanReveal && <>
              {/* Your Business (clean) */}
              <PositionedNode x={CLEAN_BUSINESS.x} y={CLEAN_BUSINESS.y} data-id="element-1530">
                <motion.div initial={{
              opacity: 0,
              scale: 0.9,
              x: -16
            }} animate={{
              opacity: 1,
              scale: 1,
              x: 0
            }} transition={{
              duration: 0.9,
              ease: [0.16, 1, 0.3, 1]
            }} className="relative" style={{
              width: CENTER_W,
              height: CENTER_H
            }} data-id="element-1531">
                  <div className="absolute -inset-3 bg-white/5 rounded-2xl blur-xl" data-id="element-1532" />
                  <div className="relative rounded-2xl bg-[#0F172A]/95 backdrop-blur-xl border border-white/20 shadow-[0_8px_40px_rgba(0,0,0,0.6)] flex items-center gap-3 px-6 py-4" style={{
                width: CENTER_W,
                height: CENTER_H
              }} data-id="element-1533">
                    <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0" data-id="element-1534">
                      <Building2 className="w-5 h-5 text-white" strokeWidth={2.2} data-id="element-1535" />
                    </div>
                    <div className="min-w-0" data-id="element-1536">
                      <div className="text-[10px] font-bold tracking-[0.28em] text-white/55 uppercase" data-id="element-1537">
                        Core
                      </div>
                      <div className="text-lg font-semibold text-white tracking-tight leading-tight whitespace-nowrap" data-id="element-1538">
                        Your Business
                      </div>
                    </div>
                  </div>
                </motion.div>
              </PositionedNode>

              {/* VEXU (clean — premium) */}
              <PositionedNode x={CLEAN_VEXU.x} y={CLEAN_VEXU.y} data-id="element-1539">
                <motion.div initial={{
              opacity: 0,
              scale: 0.9,
              x: 16
            }} animate={{
              opacity: 1,
              scale: 1,
              x: 0
            }} transition={{
              duration: 0.9,
              ease: [0.16, 1, 0.3, 1]
            }} className="relative" style={{
              width: VEXU_W,
              height: VEXU_H
            }} data-id="element-1540">
                  {/* Soft halo */}
                  <div className="absolute -inset-8 rounded-full" style={{
                background: 'radial-gradient(circle, rgba(16,217,106,0.35) 0%, rgba(16,217,106,0) 70%)',
                filter: 'blur(8px)'
              }} data-id="element-1541" />
                  <motion.div animate={{
                scale: [1, 1.12, 1],
                opacity: [0.45, 0, 0.45]
              }} transition={{
                duration: 2.8,
                repeat: Infinity,
                ease: 'easeOut'
              }} className="absolute inset-0 rounded-2xl border border-emerald-300/50" data-id="element-1542" />
                  <div className="relative rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-[0_0_70px_rgba(16,217,106,0.55)] border border-emerald-300/50 flex items-center gap-3 px-7" style={{
                width: VEXU_W,
                height: VEXU_H
              }} data-id="element-1543">
                    <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center shrink-0" data-id="element-1544">
                      <Sparkles className="w-6 h-6 text-white" strokeWidth={2.2} data-id="element-1545" />
                    </div>
                    <div className="min-w-0" data-id="element-1546">
                      <div className="text-[10px] font-bold tracking-[0.32em] text-white/85 uppercase mb-0.5" data-id="element-1547">
                        One Platform
                      </div>
                      <div className="text-2xl font-bold text-white tracking-tight leading-tight whitespace-nowrap" data-id="element-1548">
                        VEXU Platform
                      </div>
                      <div className="text-[10px] font-medium tracking-[0.2em] text-emerald-100/80 mt-0.5 uppercase" data-id="element-1549">
                        All systems · One place
                      </div>
                    </div>
                  </div>
                </motion.div>
              </PositionedNode>

              {/* Three lines: One platform / One integration / One owner */}
              <AnimatePresence data-id="element-1550">
                {cleanLines && elapsed < T.IMPACT_1 && <motion.div initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.7
            }} className="absolute" style={{
              left: 0,
              right: 0,
              top: 150,
              display: 'flex',
              justifyContent: 'center',
              gap: 28
            }} data-id="element-1551">
                    {['One platform.', 'One integration.', 'One owner.'].map((line, i) => <motion.div key={line} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: i * 0.25,
                ease: [0.16, 1, 0.3, 1]
              }} className="font-display text-2xl md:text-3xl font-medium text-white/95 tracking-tight" data-id="element-1552">
                          {line}
                        </motion.div>)}
                  </motion.div>}
              </AnimatePresence>

              {/* Micro tags around VEXU */}
              <AnimatePresence data-id="element-1553">
                {tagsVisible && elapsed < T.IMPACT_1 && <>
                    {VEXU_TAGS.map((tag, i) => <PositionedNode key={tag.label} x={CLEAN_VEXU.x + tag.dx} y={CLEAN_VEXU.y + tag.dy} data-id="element-1554">
                        <motion.div initial={{
                  opacity: 0,
                  scale: 0.85
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.9
                }} transition={{
                  duration: 0.5,
                  delay: i * 0.12,
                  ease: [0.16, 1, 0.3, 1]
                }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] backdrop-blur-xl border border-emerald-400/25 whitespace-nowrap shadow-[0_0_18px_rgba(16,217,106,0.12)]" data-id="element-1555">
                          <span className="w-1 h-1 rounded-full bg-emerald-400" data-id="element-1556" />
                          <span className="text-[10px] font-semibold tracking-wide text-emerald-50" data-id="element-1557">
                            {tag.label}
                          </span>
                        </motion.div>
                      </PositionedNode>)}
                  </>}
              </AnimatePresence>

              {/* Final impact lines */}
              <AnimatePresence data-id="element-1558">
                {elapsed >= T.IMPACT_1 && <motion.div key="impact" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.8
            }} className="absolute inset-0 flex flex-col items-center justify-end pb-24" style={{
              zIndex: 28
            }} data-id="element-1559">
                    <div className="text-center space-y-3" data-id="element-1560">
                      <motion.div initial={{
                  opacity: 0,
                  y: 16,
                  filter: 'blur(8px)'
                }} animate={{
                  opacity: 1,
                  y: 0,
                  filter: 'blur(0px)'
                }} transition={{
                  duration: 0.9,
                  ease: [0.16, 1, 0.3, 1]
                }} className="font-display text-4xl md:text-5xl tracking-tight" data-id="element-1561">
                        <span className="text-white/55 italic" data-id="element-1562">
                          From complexity…
                        </span>
                      </motion.div>
                      <motion.div initial={{
                  opacity: 0,
                  y: 16,
                  filter: 'blur(8px)'
                }} animate={{
                  opacity: elapsed >= T.IMPACT_2 ? 1 : 0,
                  y: elapsed >= T.IMPACT_2 ? 0 : 16,
                  filter: elapsed >= T.IMPACT_2 ? 'blur(0px)' : 'blur(8px)'
                }} transition={{
                  duration: 0.9,
                  ease: [0.16, 1, 0.3, 1]
                }} className="font-display text-5xl md:text-6xl font-bold tracking-tight" data-id="element-1563">
                        <span style={{
                    background: 'linear-gradient(135deg, #10D96A 0%, #6EE7B7 60%, #ffffff 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }} data-id="element-1564">
                          …to complete control.
                        </span>
                      </motion.div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </>}
        </div>
      </div>
    </div>;
}
// =================== Helpers ===================
function ScaleVar() {
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const scale = Math.min(w / STAGE_W, h / STAGE_H, 1);
      document.documentElement.style.setProperty('--stage-scale', String(scale));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return null;
}
function PositionedNode({
  x,
  y,
  children
}: {
  x: number;
  y: number;
  children: React.ReactNode;
}) {
  return <div className="absolute" style={{
    left: x,
    top: y,
    transform: 'translate(-50%, -50%)'
  }} data-id="element-1565">
      {children}
    </div>;
}